import { useMemo } from "react";
import type { ShopifySectionWidgetProps } from "../experience-views/sales-order/types";
import { getShopifyExperienceWidgets } from "../experience-views/registry";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { getExperienceWidget } from "@/lib/views/widgets";

/**
 * Shopify Style Renderer
 * 
 * Pure router component that delegates to section-specific widgets.
 * Each widget manages its own modal state and rendering.
 * 
 * Widget selection:
 * 1. DocViewSchema path: If section has _widgetId, use central EXPERIENCE_WIDGETS registry
 * 2. Legacy path: Use doctype-specific getShopifyExperienceWidgets registry
 * 
 * This ensures backward compatibility while enabling schema-driven widget selection.
 */
export function ShopifyStyleRenderer(props: ShopifySectionWidgetProps) {
  const { section } = props;
  const { doctype } = useDynamicView(); // Get doctype from context
  
  // Legacy path: Use doctype-specific widget registry (must be unconditionally called to satisfy hooks rules)
  const { map: widgetMap, fallback } = useMemo(
    () => getShopifyExperienceWidgets(doctype),
    [doctype]
  );

  // DocViewSchema v1: Check for _widgetId from schema
  // This is the AI-first path - schema specifies exactly which widget to use
  const schemaWidget = useMemo(() => {
    const widgetId = (section as any)._widgetId;
    if (widgetId) {
      return getExperienceWidget(widgetId);
    }
    return null;
  }, [section]);

  // If schema specifies a widget, use it
  if (schemaWidget) {
    const Widget = schemaWidget;
    return <Widget {...props} />;
  }
  
  // Get widget for this section
  // Try multiple keys: section.name, section.id, normalized label
  // @ts-expect-error - Property may not exist on type
  const nameKey = section.name ?? section.id;
  const labelKey = section.label 
    ? section.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    : null;
  
  // Try exact match first, then normalized label match, then lowercase
  const Widget = widgetMap[nameKey] 
    ?? (labelKey ? widgetMap[labelKey] : null)
    ?? widgetMap[nameKey?.toLowerCase()]
    ?? (nameKey ? widgetMap[nameKey.replace(/_/g, '-')] : null)
    ?? fallback;
  
  return <Widget {...props} />;
}
